import { ClientNote } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusIcon, Pencil, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDistanceToNow } from "date-fns";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type NotesPanelProps = {
  notes: ClientNote[];
  clientId: number;
};

export function NotesPanel({ notes, clientId }: NotesPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const [noteContent, setNoteContent] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const addNote = useMutation({
    mutationFn: async (content: string) => apiRequest('POST', '/api/client-notes', { clientId, content }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/notes`] });
      setIsAddDialogOpen(false);
      setNoteContent('');
      toast({ title: 'Note added' });
    },
    onError: () => toast({ title: 'Failed to add note', variant: 'destructive' })
  });

  const updateNote = useMutation({
    mutationFn: async ({ id, content }: { id: number, content: string }) => apiRequest('PATCH', `/api/client-notes/${id}`, { content }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/notes`] });
      setIsEditDialogOpen(false);
      setSelectedNoteId(null);
      setNoteContent('');
      toast({ title: 'Note updated' });
    },
    onError: () => toast({ title: 'Failed to update note', variant: 'destructive' })
  });

  const deleteNote = useMutation({
    mutationFn: async (id: number) => apiRequest('DELETE', `/api/client-notes/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/notes`] });
      setIsDeleteDialogOpen(false);
      setSelectedNoteId(null);
      toast({ title: 'Note deleted' });
    },
    onError: () => toast({ title: 'Failed to delete note', variant: 'destructive' })
  });
  
  const selectedNote = selectedNoteId 
    ? notes.find(note => note.id === selectedNoteId) 
    : null;

  function handleAddNote() {
    setNoteContent("");
    setIsAddDialogOpen(true);
  }

  function handleEditNote(note: ClientNote) {
    setSelectedNoteId(note.id);
    setNoteContent(note.content);
    setIsEditDialogOpen(true);
  }

  function handleDeleteNote(note: ClientNote) {
    setSelectedNoteId(note.id);
    setIsDeleteDialogOpen(true);
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Notes</h3>
        <Button size="sm" onClick={handleAddNote}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </div>

      {notes.length === 0 ? (
        <Alert>
          <AlertDescription>
            No notes have been added for this contact yet.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
                    {note.updatedAt > note.createdAt && 
                      ` (edited ${formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })})`}
                  </p>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
                          <path d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                        </svg>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditNote(note)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        <span>Edit</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteNote(note)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Delete</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <p className="whitespace-pre-wrap">{note.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Note Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a new note for this contact.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note-content">Note Content</Label>
              <Textarea
                id="note-content"
                placeholder="Enter note content..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="submit" disabled={!noteContent.trim()} onClick={() => addNote.mutate(noteContent)}>
              {addNote.isPending ? 'Adding…' : 'Add Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>
              Update the content of this note.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-note-content">Note Content</Label>
              <Textarea
                id="edit-note-content"
                placeholder="Enter note content..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              type="submit" 
              disabled={!noteContent.trim() || noteContent === selectedNote?.content}
              onClick={() => selectedNote && updateNote.mutate({ id: selectedNote.id!, content: noteContent })}
            >
              {updateNote.isPending ? 'Updating…' : 'Update Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Note Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Note</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedNote && (
            <div className="py-4 border rounded-md p-3 bg-muted/50">
              <p className="text-sm whitespace-pre-wrap line-clamp-3">{selectedNote.content}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => selectedNote && deleteNote.mutate(selectedNote.id!)}
            >
              {deleteNote.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
